(() => {
  'use strict';

  const money = (cents, symbol) => {
    const value = (Math.round(cents) / 100).toFixed(2).replace('.', ',');
    return `${value}${symbol || '€'}`;
  };

  const stripEmoji = (text) =>
    String(text)
      .replace(/[☀-➿️]|[\u{1f300}-\u{1faff}]/gu, '')
      .replace(/\s+/g, ' ')
      .trim();

  const whenSwiper = (run) => {
    if (typeof window.Swiper !== 'undefined') return run();
    let tries = 0;
    const timer = setInterval(() => {
      if (typeof window.Swiper !== 'undefined') {
        clearInterval(timer);
        run();
      } else if (++tries > 100) {
        clearInterval(timer);
      }
    }, 50);
  };

  const optionIndex = (names, wanted) =>
    names.findIndex((n) => String(n).trim() === String(wanted || '').trim());

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.pack = 'simple';
      this.hydrate();

      this.addEventListener(
        'click',
        (event) => {
          const switchLink = event.target.closest('[data-pp-switch]');
          if (switchLink && this.contains(switchLink)) return this.onSwitch(event, switchLink);
          const optionBtn = event.target.closest('[data-pp-option]');
          if (optionBtn && this.contains(optionBtn)) return this.onOptionClick(optionBtn);
          const qtyBtn = event.target.closest('[data-pp-qty-step]');
          if (qtyBtn && this.contains(qtyBtn)) return this.onQtyStep(qtyBtn);
          const addBtn = event.target.closest('[data-pp-add]');
          if (addBtn && this.contains(addBtn)) return this.onAdd(addBtn);
          const packBtn = event.target.closest('[data-pp-pack]');
          if (packBtn && this.contains(packBtn)) return this.onPack(packBtn);
          const intervalBtn = event.target.closest('[data-pp-sub-interval]');
          if (intervalBtn && this.contains(intervalBtn)) return this.onSubInterval(intervalBtn);
          const upsellAdd = event.target.closest('[data-pp-upsell-add]');
          if (upsellAdd && this.contains(upsellAdd)) return this.onUpsellAdd(upsellAdd);
        },
        { signal }
      );

      this.addEventListener(
        'input',
        (event) => {
          const qty = event.target.closest('[data-pp-qty-value]');
          if (qty && this.contains(qty)) this.syncQtyWidth(qty);
        },
        { signal }
      );

      this.addEventListener(
        'change',
        (event) => {
          const sub = event.target.closest('[data-pp-subscribe]');
          if (sub && this.contains(sub)) return this.onSubscribe(sub);
          const up = event.target.closest('[data-pp-upsell-check]');
          if (up && this.contains(up)) return this.updateUpsellTotal();
          const second = event.target.closest('[data-pp-pack-second]');
          if (second && this.contains(second)) this.packSecond = Number(second.value) || null;
        },
        { signal }
      );

      this.subPlansByVariant = this.readSubPlans();
      this.render();
      this.updateUpsellTotal();

      if (window.history?.replaceState && !window.history.state) {
        window.history.replaceState({ ppSwitch: true }, '', window.location.href);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.switchController?.abort();
    }

    hydrate() {
      this.data = this.readProduct();
      this.symbol = this.dataset.currency || '€';
      this.optionNames = (this.data && this.data.option_names) || [];
      this.variants = (this.data && this.data.variants) || [];
      this.sellingPlan = null;
      this.subPlan = null;
      // La carte variante -> plans appartient au produit affiche : sans cette
      // relecture, un changement de produit laissait l'ancienne en place et
      // les pastilles de frequence disparaissaient.
      this.subPlansByVariant = this.readSubPlans();

      const startId = Number(this.dataset.currentVariant);
      this.variant =
        this.variants.find((v) => v.id === startId) ||
        this.variants.find((v) => v.available) ||
        this.variants[0] ||
        null;
      this.selected = this.variant ? this.variant.options.slice() : [];

      const secondSel = this.querySelector('[data-pp-pack-second]');
      this.packSecond = secondSel ? Number(secondSel.value) || null : null;

      this.querySelectorAll('[data-pp-pack]').forEach((el) => {
        const sel = el.dataset.ppPack === this.pack;
        el.classList.toggle('is-selected', sel);
        el.setAttribute('aria-pressed', sel ? 'true' : 'false');
      });

      this.querySelectorAll('pp-gallery').forEach((g) =>
        g.dispatchEvent(new CustomEvent('pp:hydrate'))
      );

      whenSwiper(() => this.initCarousels());
    }

    initCarousels() {
      this.querySelectorAll('[data-pp-swiper]').forEach((el) => {
        if (el.swiper) {
          el.swiper.destroy(true, true);
          el.swiper = null;
        }
        const wrapper = el.querySelector('.swiper-wrapper');
        if (!wrapper) return;
        const wrapped = wrapper.querySelector(':scope > .shopify-block');
        const scope = el.parentElement || el;
        const prev = scope.querySelector('[data-pp-swiper-prev]');
        const next = scope.querySelector('[data-pp-swiper-next]');
        const perView = el.dataset.ppSwiperPerView;
        const options = {
          slidesPerView: perView ? Number(perView) : 'auto',
          spaceBetween: Number(el.dataset.ppSwiperGap) || 12,
          slideClass: wrapped ? 'shopify-block' : 'swiper-slide',
          watchOverflow: true,
          observer: true,
          observeParents: true,
          resizeObserver: true,
        };
        if (!perView) options.freeMode = { enabled: true, momentumBounce: false };
        if (prev && next) options.navigation = { prevEl: prev, nextEl: next };
        const pagination = el.dataset.ppSwiperPagination
          ? scope.querySelector(el.dataset.ppSwiperPagination)
          : null;
        if (pagination) {
          options.pagination = { el: pagination, clickable: true, bulletClass: 'pp-dot', bulletActiveClass: 'is-active' };
        }
        const autoplay = Number(el.dataset.ppSwiperAutoplay);
        if (autoplay > 0) options.autoplay = { delay: autoplay, disableOnInteraction: false, pauseOnMouseEnter: true };
        if (el.dataset.ppSwiperLoop === 'true') options.loop = true;
        el.swiper = new window.Swiper(el, options);
      });
    }

    readProduct() {
      const script = this.querySelector('[data-pp-product]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    onSwitch(event, link) {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      const sectionId = this.dataset.sectionId;
      const href = link.getAttribute('href');
      if (!sectionId || !href || typeof window.morphdom !== 'function') return;
      if (link.getAttribute('aria-current') === 'true') {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      this.switchTo(href, link);
    }

    async switchTo(href, link, options) {
      this.switchController?.abort();
      this.switchController = new AbortController();
      const { signal } = this.switchController;

      const url = new URL(href, window.location.origin);
      this.setAttribute('aria-busy', 'true');
      if (link) link.classList.add('is-loading');

      try {
        const fetchUrl = new URL(url);
        fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
        const res = await fetch(fetchUrl, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('product-form');
        if (!next) throw new Error('product-form absent de la reponse');

        window.morphdom(this, next, {
          onBeforeElUpdated: (from, to) => !from.isEqualNode(to),
        });

        this.hydrate();
        this.render();
        this.updateUpsellTotal();

        if (options?.push !== false && window.history?.pushState) {
          window.history.pushState({ ppSwitch: true }, '', url);
        }
        if (this.dataset.pageTitle) document.title = this.dataset.pageTitle;

        this.dispatchEvent(new CustomEvent('pp:switched', { bubbles: true, detail: { url: String(url) } }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(String(url));
      } finally {
        this.removeAttribute('aria-busy');
        if (link) link.classList.remove('is-loading');
      }
    }

    resolveOptionIndex(name, value) {
      const names = this.optionNames || [];
      const wanted = String(name || '').trim().toLowerCase();
      let index = names.findIndex((n) => String(n).trim().toLowerCase() === wanted);
      if (index >= 0) return index;
      if (value != null) {
        index = names.findIndex((_, i) => this.variants.some((v) => v.options[i] === value));
        if (index >= 0) return index;
      }
      return names.length === 1 ? 0 : -1;
    }

    onOptionClick(button) {
      const name = button.dataset.ppOptionName;
      const value = button.dataset.ppValue;
      if (value == null) return;
      const index = this.resolveOptionIndex(name, value);
      if (index < 0) return;
      if (this.selected[index] === value) return;
      this.selected[index] = value;
      this.resolveVariant();
      this.render();
      this.syncUrl();
    }

    resolveVariant() {
      const exact = this.variants.find((v) => v.options.every((opt, i) => opt === this.selected[i]));
      if (exact) {
        this.variant = exact;
        return;
      }
      const partial = this.variants.find(
        (v) => v.available && this.selected.every((val, i) => val == null || v.options[i] === val)
      );
      this.variant = partial || this.variant;
      if (this.variant) this.selected = this.variant.options.slice();
    }

    get quantity() {
      const el = this.querySelector('[data-pp-qty-value]');
      const n = el ? parseInt(el.value ?? el.textContent, 10) : 1;
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    onQtyStep(button) {
      const el = this.querySelector('[data-pp-qty-value]');
      if (!el) return;
      const delta = button.dataset.ppQtyStep === 'minus' ? -1 : 1;
      const next = Math.max(1, this.quantity + delta);
      if ('value' in el && el.tagName === 'INPUT') el.value = String(next);
      else el.textContent = String(next);
      this.syncQtyWidth(el);
    }

    syncQtyWidth(el) {
      const len = String(el.value ?? el.textContent ?? '').trim().length || 1;
      el.style.setProperty('--pp-qty-len', String(len));
    }

    onPack(button) {
      this.pack = button.dataset.ppPack;
      this.querySelectorAll('[data-pp-pack]').forEach((b) => {
        const sel = b === button;
        b.classList.toggle('is-selected', sel);
        b.setAttribute('aria-pressed', sel ? 'true' : 'false');
      });
      if (this.pack === 'double' && !this.packSecond) {
        const sel = this.querySelector('[data-pp-pack-second]');
        if (sel) this.packSecond = Number(sel.value) || null;
      }
      this.render();
    }

    readSubPlans() {
      const node = this.querySelector('[data-pp-sub-plans]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }
    }

    renderSubPlans() {
      const carte = this.subPlansByVariant;
      const v = this.variant;
      if (!carte || !v) return;
      const groupe = this.querySelector('.pp-sub__intervals');
      if (!groupe) return;

      const plans = carte[String(v.id)] || [];

      // Aucune entree pour cette variante : on garde ce que le serveur a rendu
      // plutot que de vider les pastilles. On se contente de resynchroniser
      // l'etat a partir de la pastille deja selectionnee dans le DOM.
      if (!plans.length) {
        const dejaLa = groupe.querySelector('[data-pp-sub-plan].is-selected')
          || groupe.querySelector('[data-pp-sub-plan]');
        this.subPlan = dejaLa ? Number(dejaLa.dataset.ppSubPlan) || null : null;
        const b = this.querySelector('[data-pp-subscribe]');
        if (b && b.checked) this.sellingPlan = this.subPlan;
        return;
      }
      const actuel = groupe.querySelector('[data-pp-sub-plan].is-selected');
      const garde = actuel ? actuel.dataset.ppSubPlan : null;

      const anciens = Array.from(groupe.querySelectorAll('[data-pp-sub-plan]'));
      if (anciens.length === plans.length && anciens.every((b, i) => b.dataset.ppSubPlan === String(plans[i].id))) return;

      anciens.forEach((b) => b.remove());

      let choisi = null;
      plans.forEach((plan, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pp-sub__interval';
        b.setAttribute('role', 'radio');
        b.dataset.ppSubInterval = plan.name;
        b.dataset.ppSubPlan = String(plan.id);
        b.textContent = plan.name;
        const actif = garde ? String(plan.id) === garde : i === 0;
        if (actif) choisi = b;
        groupe.appendChild(b);
      });

      if (!choisi) choisi = groupe.querySelector('[data-pp-sub-plan]');
      groupe.querySelectorAll('[data-pp-sub-plan]').forEach((b) => {
        const sel = b === choisi;
        b.classList.toggle('is-selected', sel);
        b.setAttribute('aria-checked', sel ? 'true' : 'false');
      });

      this.subPlan = choisi ? Number(choisi.dataset.ppSubPlan) || null : null;
      const box = this.querySelector('[data-pp-subscribe]');
      if (box) {
        box.value = this.subPlan ? String(this.subPlan) : '';
        if (box.checked) this.sellingPlan = this.subPlan;
      }
    }

    onSubscribe(input) {
      this.sellingPlan = input.checked ? this.subPlan || Number(input.value) || null : null;
      this.render();
    }

    onSubInterval(button) {
      this.subInterval = button.dataset.ppSubInterval || null;
      const plan = Number(button.dataset.ppSubPlan) || null;
      if (plan) {
        this.subPlan = plan;
        const box = this.querySelector('[data-pp-subscribe]');
        if (box?.checked) {
          this.sellingPlan = plan;
          this.render();
        }
      }
      this.querySelectorAll('[data-pp-sub-interval]').forEach((b) => {
        const sel = b === button;
        b.classList.toggle('is-selected', sel);
        b.setAttribute('aria-checked', sel ? 'true' : 'false');
      });
    }

    updateUpsellTotal() {
      this.querySelectorAll('[data-pp-upsell]').forEach((scope) => {
        let total = 0;
        scope.querySelectorAll('[data-pp-upsell-item]').forEach((item) => {
          const cb = item.querySelector('[data-pp-upsell-check]');
          const checked = !!(cb && cb.checked);
          item.classList.toggle('is-checked', checked);
          if (checked) total += Number(item.dataset.price) || 0;
        });
        const out = scope.querySelector('[data-pp-upsell-total]');
        if (out) out.textContent = money(total, this.symbol);
      });
    }

    onUpsellAdd(button) {
      const scope = button.closest('[data-pp-upsell]');
      if (!scope) return;
      const items = Array.from(scope.querySelectorAll('[data-pp-upsell-check]:checked'))
        .map((cb) => ({ id: Number(cb.value), quantity: 1 }))
        .filter((i) => i.id);
      if (!items.length) return;
      this.postCart(items, button);
    }

    render() {
      this.querySelectorAll('[data-pp-option]').forEach((btn) => {
        const index = this.resolveOptionIndex(btn.dataset.ppOptionName, btn.dataset.ppValue);
        const isSelected = index >= 0 && this.selected[index] === btn.dataset.ppValue;
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      this.querySelectorAll('[data-pp-title]').forEach((el) => {
        const index = optionIndex(this.optionNames, el.dataset.ppTitleOption);
        if (index < 0) return;
        const value = stripEmoji(this.selected[index] || '');
        el.textContent = `${el.dataset.ppTitlePrefix || ''}${value}`.trim();
      });

      const v = this.variant;
      if (!v) return;

      this.renderSubPlans();

      const packGroup = this.querySelector('[data-pp-pack-group]');
      const packDiscount = packGroup ? Number(packGroup.dataset.ppPackDiscount) || 0 : 0;
      const unitSuffix = packGroup ? packGroup.dataset.ppPackUnitSuffix || '' : '';
      const servings = Number(v.servings) || 0;

      let selectedNet = v.price;
      let selectedGross = v.price;

      this.querySelectorAll('[data-pp-pack]').forEach((el) => {
        const qty = Number(el.dataset.ppPackQty) || 1;
        const gross = v.price * qty;
        const net = qty > 1 ? Math.round(gross * (1 - packDiscount / 100)) : gross;

        const amount = el.querySelector('[data-pp-pack-amount]');
        if (amount) amount.textContent = money(net, this.symbol);

        const compare = el.querySelector('[data-pp-pack-compare]');
        if (compare) {
          const show = net < gross;
          compare.textContent = show ? money(gross, this.symbol) : '';
          compare.hidden = !show;
        }

        const unit = el.querySelector('[data-pp-pack-unit]');
        if (unit) {
          unit.textContent = servings > 0 ? money(net / (servings * qty), this.symbol) + unitSuffix : '';
        }

        if (el.dataset.ppPack === this.pack) {
          selectedNet = net;
          selectedGross = gross;
        }
      });

      this.querySelectorAll('[data-pp-price]').forEach((el) => {
        el.textContent = money(selectedNet, this.symbol);
      });

      let compareText = '';
      if (selectedNet < selectedGross) {
        compareText = money(selectedGross, this.symbol);
      } else if (v.compare_at_price && v.compare_at_price > v.price) {
        const qty = selectedGross / v.price;
        compareText = money(v.compare_at_price * qty, this.symbol);
      }
      this.querySelectorAll('[data-pp-compare]').forEach((el) => {
        el.textContent = compareText;
        el.hidden = !compareText;
      });

      this.querySelectorAll('[data-pp-klarna]').forEach((el) => {
        const split = Number(el.dataset.ppKlarnaSplit) || 3;
        el.textContent = money(selectedNet / split, this.symbol);
      });

      this.querySelectorAll('[data-pp-add]').forEach((btn) => {
        btn.disabled = !v.available;
        btn.classList.toggle('is-disabled', !v.available);
      });

      this.dataset.currentVariant = String(v.id);

      if (v.featured_media) {
        this.dispatchEvent(
          new CustomEvent('pp:variant', {
            bubbles: false,
            detail: { variant: v, mediaPosition: v.featured_media },
          })
        );
        this.querySelectorAll('[data-pp-gallery]').forEach((g) =>
          g.dispatchEvent(new CustomEvent('pp:gallery-go', { detail: { position: v.featured_media } }))
        );
      }
    }

    syncUrl() {
      if (!this.variant || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.variant.id);
      window.history.replaceState({}, '', url);
    }

    // Plan de la seconde saveur, a la meme frequence que la premiere.
    // Un plan appartient a un produit : on ne peut pas reutiliser l'id du
    // premier, il faut celui que porte la variante choisie.
    planPourSecond(variantId) {
      if (!this.sellingPlan) return null;
      const sel = this.querySelector('[data-pp-pack-second]');
      if (!sel) return null;
      const opt = Array.from(sel.options).find((o) => String(o.value) === String(variantId));
      let plans = [];
      try { plans = JSON.parse(opt?.dataset.ppPlans || '[]'); } catch (e) { plans = []; }
      if (!plans.length) return null;
      const meme = this.subInterval
        ? plans.find((x) => x.name === this.subInterval)
        : null;
      return (meme || plans[0]).id;
    }

    extraItems() {
      const items = [];
      if (this.pack === 'double') {
        const second = this.packSecond || (this.variant && this.variant.id);
        if (!second) return items;
        const ligne = { id: second, quantity: this.quantity };
        const plan = this.planPourSecond(second);
        if (plan) {
          ligne.selling_plan = plan;
          if (this.subInterval) ligne.properties = { 'Fréquence': this.subInterval };
        }
        items.push(ligne);
      }
      return items;
    }

    onAdd(button) {
      if (!this.variant || !this.variant.available) return;
      const item = { id: this.variant.id, quantity: this.quantity };
      if (this.sellingPlan) item.selling_plan = this.sellingPlan;
      if (this.sellingPlan && this.subInterval) {
        item.properties = { ...(item.properties || {}), Fréquence: this.subInterval };
      }
      this.postCart([item, ...this.extraItems()], button);
    }

    async postCart(items, button) {
      if (button && button.getAttribute('aria-busy') === 'true') return;
      if (button) button.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: 'w-side-cart', sections_url: window.location.pathname }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json && json.description ? json.description : 'Cart error');
        document.dispatchEvent(new CustomEvent('cart:add', { detail: { sections: json.sections, open: true } }));
      } catch (error) {
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: String(error) } }));
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  window.addEventListener('popstate', (event) => {
    if (!event.state || !event.state.ppSwitch) return;
    const form = document.querySelector('product-form[data-section-id]');
    if (form && typeof form.switchTo === 'function') form.switchTo(window.location.href, null, { push: false });
  });
})();
